import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from '@emotion/styled';
// 이모지 반응 아이콘들
const reactionIcons = {
    like: '👍',
    heart: '❤️',
    smile: '😊',
    sad: '😢',
    angry: '😠',
    surprised: '😮',
    thumbs_up: '👍',
    thumbs_down: '👎'
};
const ReactionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
  align-items: center;
`;
const ReactionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  border: 1px solid ${(props)=>props.isActive ? '#3b82f6' : '#e5e7eb'};
  background-color: ${(props)=>props.isActive ? '#eff6ff' : '#ffffff'};
  color: ${(props)=>props.isActive ? '#3b82f6' : '#6b7280'};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 1.75rem;

  &:hover {
    background-color: ${(props)=>props.isActive ? '#dbeafe' : '#f9fafb'};
    border-color: ${(props)=>props.isActive ? '#2563eb' : '#d1d5db'};
  }

  &:active {
    transform: scale(0.95);
  }

  .dark & {
    border-color: ${(props)=>props.isActive ? '#3b82f6' : '#374151'};
    background-color: ${(props)=>props.isActive ? '#1e3a8a' : '#1f2937'};
    color: ${(props)=>props.isActive ? '#93c5fd' : '#9ca3af'};

    &:hover {
      background-color: ${(props)=>props.isActive ? '#1e40af' : '#374151'};
      border-color: ${(props)=>props.isActive ? '#2563eb' : '#4b5563'};
    }
  }
`;
const ReactionEmoji = styled.span`
  font-size: 0.875rem;
  line-height: 1;
`;
const ReactionCount = styled.span`
  font-weight: 500;
  font-size: 0.75rem;
`;
const AddReactionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }

  .dark & {
    border-color: #374151;
    background-color: #1f2937;
    color: #9ca3af;

    &:hover {
      background-color: #374151;
      border-color: #4b5563;
      color: #d1d5db;
    }
  }
`;
const ReactionPicker = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 0.25rem;
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;

  .dark & {
    background-color: #1f2937;
    border-color: #374151;
  }
`;
const ReactionPickerButton = styled.button`
  padding: 0.25rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.25rem;
  font-size: 1.25rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }

  .dark &:hover {
    background-color: #374151;
  }
`;
const MessageReactions = ({ messageId, reactions, onAddReaction, onRemoveReaction, currentUserId = 'current_user', className })=>{
    const [showPicker, setShowPicker] = useState(false);
    // 반응 타입별로 그룹화하고 카운트 계산
    const groupedReactions = reactions.reduce((acc, reaction)=>{
        if (!acc[reaction.reactionType]) {
            acc[reaction.reactionType] = {
                count: 0,
                users: [],
                hasUserReacted: false
            };
        }
        acc[reaction.reactionType].count++;
        acc[reaction.reactionType].users.push(reaction);
        if (reaction.userId === currentUserId) {
            acc[reaction.reactionType].hasUserReacted = true;
        }
        return acc;
    }, {});
    const handleReactionClick = (reactionType)=>{
        const reactionGroup = groupedReactions[reactionType];
        if (reactionGroup?.hasUserReacted) {
            onRemoveReaction(messageId, reactionType);
        } else {
            onAddReaction(messageId, reactionType);
        }
    };
    const handleAddReactionClick = (reactionType)=>{
        onAddReaction(messageId, reactionType);
        setShowPicker(false);
    };
    const availableReactions = [
        'like',
        'heart',
        'smile',
        'sad',
        'angry',
        'surprised'
    ];
    if (Object.keys(groupedReactions).length === 0 && !showPicker) {
        return /*#__PURE__*/ _jsx(ReactionContainer, {
            className: className,
            children: /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'relative'
                },
                children: [
                    /*#__PURE__*/ _jsx(AddReactionButton, {
                        onClick: ()=>setShowPicker(!showPicker),
                        title: "반응 추가",
                        children: "+"
                    }),
                    showPicker && /*#__PURE__*/ _jsx(ReactionPicker, {
                        children: availableReactions.map((reactionType)=>/*#__PURE__*/ _jsx(ReactionPickerButton, {
                                onClick: ()=>handleAddReactionClick(reactionType),
                                title: reactionType,
                                children: reactionIcons[reactionType] || '👍'
                            }, reactionType))
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs(ReactionContainer, {
        className: className,
        children: [
            Object.entries(groupedReactions).map(([reactionType, data])=>/*#__PURE__*/ _jsxs(ReactionButton, {
                    isActive: data.hasUserReacted,
                    onClick: ()=>handleReactionClick(reactionType),
                    title: `${data.users.map((u)=>u.displayName).join(', ')}가 ${reactionType} 반응을 했습니다`,
                    "aria-label": data.hasUserReacted ? `${reactionType} 반응 제거` : `${reactionType} 반응 추가`,
                    children: [
                        /*#__PURE__*/ _jsx(ReactionEmoji, {
                            children: reactionIcons[reactionType] || '👍'
                        }),
                        data.count > 0 && /*#__PURE__*/ _jsx(ReactionCount, {
                            children: data.count
                        })
                    ]
                }, reactionType)),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'relative'
                },
                children: [
                    /*#__PURE__*/ _jsx(AddReactionButton, {
                        onClick: ()=>setShowPicker(!showPicker),
                        title: "반응 추가",
                        children: "+"
                    }),
                    showPicker && /*#__PURE__*/ _jsx(ReactionPicker, {
                        children: availableReactions.map((reactionType)=>/*#__PURE__*/ _jsx(ReactionPickerButton, {
                                onClick: ()=>handleAddReactionClick(reactionType),
                                title: reactionType,
                                children: reactionIcons[reactionType] || '👍'
                            }, reactionType))
                    })
                ]
            })
        ]
    });
};
export default MessageReactions;
